/**
 * Shared hex colour validation and normalization utilities.
 *
 * All colour values in NocoDB are stored as uppercase 6-digit hex strings
 * with a leading `#` (e.g. `#FF5733`). These helpers centralise the regex
 * and normalisation logic so that the SDK column helper, the backend field
 * handler, and the frontend components all use the same rules.
 */
/** Matches a 6-digit hex colour with an optional `#` prefix. */
export const HEX_COLOUR_REGEX = /^#?([0-9A-Fa-f]{6})$/;
/**
 * Matches a 6-digit hex colour with an optional `#` prefix and an optional
 * 2-digit alpha suffix.  The alpha channel is captured but stripped during
 * normalisation so that the stored value is always 6 digits.
 */
export const HEX_COLOUR_WITH_ALPHA_REGEX = /^#?([0-9A-Fa-f]{6})([0-9A-Fa-f]{2})?$/;
/**
 * Validate and normalise a raw value to `#RRGGBB` (uppercase).
 *
 * @param value – Any value that might represent a hex colour.
 * @returns The normalised `#RRGGBB` string, or `null` when the input is
 *          empty / invalid.
 */
export function normalizeHexColour(value) {
    if (!value)
        return null;
    const str = String(value).trim();
    if (!str)
        return null;
    const match = str.match(HEX_COLOUR_REGEX);
    return match ? `#${match[1].toUpperCase()}` : null;
}
/**
 * Same as {@link normalizeHexColour} but also accepts 8-digit hex values
 * (with a 2-digit alpha suffix) and strips the alpha channel.
 */
export function normalizeHexColourWithAlpha(value) {
    if (!value)
        return null;
    const str = String(value).trim();
    if (!str)
        return null;
    const match = str.match(HEX_COLOUR_WITH_ALPHA_REGEX);
    return match ? `#${match[1].toUpperCase()}` : null;
}
/**
 * Check whether a string is a valid `#RRGGBB` colour (requires `#` prefix).
 */
export function isValidHexColour(value) {
    return typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value);
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY29sb3VyLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vLi4vc3JjL2xpYi9jb2x1bW5IZWxwZXIvdXRpbHMvY29sb3VyLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBOzs7Ozs7O0dBT0c7QUFFSCxnRUFBZ0U7QUFDaEUsTUFBTSxDQUFDLE1BQU0sZ0JBQWdCLEdBQUcsc0JBQXNCLENBQUM7QUFFdkQ7Ozs7R0FJRztBQUNILE1BQU0sQ0FBQyxNQUFNLDJCQUEyQixHQUN0Qyx1Q0FBdUMsQ0FBQztBQUUxQzs7Ozs7O0dBTUc7QUFDSCxNQUFNLFVBQVUsa0JBQWtCLENBQUMsS0FBYztJQUMvQyxJQUFJLENBQUMsS0FBSztRQUFFLE9BQU8sSUFBSSxDQUFDO0lBRXhCLE1BQU0sR0FBRyxHQUFHLE1BQU0sQ0FBQyxLQUFLLENBQUMsQ0FBQyxJQUFJLEVBQUUsQ0FBQztJQUNqQyxJQUFJLENBQUMsR0FBRztRQUFFLE9BQU8sSUFBSSxDQUFDO0lBRXRCLE1BQU0sS0FBSyxHQUFHLEdBQUcsQ0FBQyxLQUFLLENBQUMsZ0JBQWdCLENBQUMsQ0FBQztJQUMxQyxPQUFPLEtBQUssQ0FBQyxDQUFDLENBQUMsSUFBSSxLQUFLLENBQUMsQ0FBQyxDQUFDLENBQUMsV0FBVyxFQUFFLEVBQUUsQ0FBQyxDQUFDLENBQUMsSUFBSSxDQUFDO0FBQ3JELENBQUM7QUFFRDs7O0dBR0c7QUFDSCxNQUFNLFVBQVUsMkJBQTJCLENBQUMsS0FBYztJQUN4RCxJQUFJLENBQUMsS0FBSztRQUFFLE9BQU8sSUFBSSxDQUFDO0lBRXhCLE1BQU0sR0FBRyxHQUFHLE1BQU0sQ0FBQyxLQUFLLENBQUMsQ0FBQyxJQUFJLEVBQUUsQ0FBQztJQUNqQyxJQUFJLENBQUMsR0FBRztRQUFFLE9BQU8sSUFBSSxDQUFDO0lBRXRCLE1BQU0sS0FBSyxHQUFHLEdBQUcsQ0FBQyxLQUFLLENBQUMsMkJBQTJCLENBQUMsQ0FBQztJQUNyRCxPQUFPLEtBQUssQ0FBQyxDQUFDLENBQUMsSUFBSSxLQUFLLENBQUMsQ0FBQyxDQUFDLENBQUMsV0FBVyxFQUFFLEVBQUUsQ0FBQyxDQUFDLENBQUMsSUFBSSxDQUFDO0FBQ3JELENBQUM7QUFFRDs7R0FFRztBQUNILE1BQU0sVUFBVSxnQkFBZ0IsQ0FBQyxLQUFjO0lBQzdDLE9BQU8sT0FBTyxLQUFLLEtBQUssUUFBUSxJQUFJLG1CQUFtQixDQUFDLElBQUksQ0FBQyxLQUFLLENBQUMsQ0FBQztBQUN0RSxDQUFDIn0=